import {LOGIN_API_TYPE} from "@/src/model/apiTypes";
import {LoginApi, LoginType} from "@/src/model/LoginApi";

const loginApi = new LoginApi()

const parseJwtPayload = (token) => {
    try {
        const payload = token.split('.')[1];
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const decoded = atob(base64);
        return JSON.parse(decoded);
    } catch (e) {
        return null;
    }
}

export default {
    namespaced: true,
    state: {
        user: null,
        loginType: null, // BASIC or SSO
        authIsReady: false,
        groups: [],
        users: [],
        roles: [],
        globalPermissions: {},
        preferences: {}
    },
    mutations: {
        setUser(state, user) {
            if (!user) {
                state.user = null
                return
            }
            state.user = {
                email: user.email || user.preferred_username || '',
                uid: user.uid || user.id || '',
                name: user.name || ''
            }
        },
        setLoginType(state, payload) {
            state.loginType = payload
            console.log('login type state change:', state.loginType)
        },
        setAuthIsReady(state, payload) {
            state.authIsReady = payload
        },
        setGroups(state, groups) {
            state.groups = groups
        },
        setUsers(state, users) {
            state.users = users
        },
        setRoles(state, roles) {
            state.roles = roles
        },
        setPreferences(state, preferences) {
            state.preferences = preferences
        }
    },
    actions: {
        async login({commit, dispatch}, {username, password}) {
            console.log('login action')
            const res = await loginApi.exec(LOGIN_API_TYPE.BASIC_LOGIN, {
                    username: username,
                    password: password
                }
            ).catch(err => {
                throw new Error('Invalid username or password')
            })
            if (res) {
                commit('setUser', res)
                console.log('User ' + res)
            }
        },
        async logout() {
            // Reload to clear all state
            location.reload()
        },
        async loginFromFabric({commit, dispatch}, {token}) {
            const claims = parseJwtPayload(token)
            if (!claims) {
                return
            }

            const user = {
                email: claims.preferred_username || claims.upn || claims.email || '',
                uid: claims.oid || claims.sub || claims.objectId || '',
                name: claims.name || '',
            }

            commit('setUser', user)
            commit('setLoginType', LoginType.FABRIC)
            commit('setAuthIsReady', true)

            try {
                const backendUser = await loginApi.exec(LOGIN_API_TYPE.GET_USER)
                if (backendUser) {
                    commit('setUser', backendUser)
                }
            } catch (error) {
                console.warn('Fabric backend user bootstrap failed:', error)
            }

            try {
                await dispatch('loadGlobalPermissions')
            } catch (error) {
                console.warn('Fabric permissions bootstrap failed:', error)
            }
        },
        async user({dispatch, commit}) {
            const res = await loginApi.exec(LOGIN_API_TYPE.GET_USER)
            commit('setUser', res)
            commit('setAuthIsReady', true)
        },
        loadUsers: async({commit}) => {
            const users = await loginApi.exec(LOGIN_API_TYPE.USERS)
            commit('setUsers', users)
        },
        loadGroups: async({commit}) => {
            const groups = await loginApi.exec(LOGIN_API_TYPE.GROUPS)
            commit('setGroups', groups)
        },
        loadRoles: async({commit}) => {
            const roles = await loginApi.exec(LOGIN_API_TYPE.ROLES)
            commit('setRoles', roles)
        },
        loadUsersAndGroups: async({commit, dispatch}) => {
            await dispatch('loadUsers')
            await dispatch('loadGroups')
            await dispatch('loadRoles')
        },
        addGroup: async({commit, dispatch}, {name}) => {
            await loginApi.exec(LOGIN_API_TYPE.ADD_GROUP, {name})
            dispatch('loadGroups')
        },
        deleteGroup: async({commit, dispatch}, id) => {
            await loginApi.exec(LOGIN_API_TYPE.DELETE_GROUP, {id})
            dispatch('loadUsersAndGroups') // reload both as the group deletion may have affected users
        },
        editGroup: async({commit, dispatch}, {id, data}) => {
            await loginApi.exec(LOGIN_API_TYPE.EDIT_GROUP, {id, data})
            dispatch('loadUsersAndGroups')
        },
        editUser: async({commit, dispatch}, {id, data}) => {
            await loginApi.exec(LOGIN_API_TYPE.EDIT_USER, {id, data})
            dispatch('loadUsersAndGroups')
        },
        transferOwnership: async({commit, dispatch}, {id, transfer_to}) => {
            await loginApi.exec(LOGIN_API_TYPE.TRANSFER_OWNERSHIP, {id, transfer_to})
        },
        deleteUser: async({commit, dispatch}, id) => {
            await loginApi.exec(LOGIN_API_TYPE.DELETE_USER, {id})
            dispatch('loadUsersAndGroups')
        },
        inviteUser: async({commit, dispatch}, data) => {
            await loginApi.exec(LOGIN_API_TYPE.INVITE_USER, data)
            dispatch('loadUsers')
        },
        loadGlobalPermissions: async({state, commit}) => {
            const perms = await loginApi.exec(LOGIN_API_TYPE.GET_GLOBAL_PERMISSIONS)
            state.globalPermissions = (perms && typeof perms === 'object') ? perms : {}
        },
        resetPassword: async({commit, dispatch}, data) => {
            if (data.hasOwnProperty('id')) {        
                await loginApi.exec(LOGIN_API_TYPE.RESET_PASSWORD, {id: data.id})
            }
            if (data.hasOwnProperty('email')) {
                await loginApi.exec(LOGIN_API_TYPE.EMAIL_RESET, {email: data.email})
            }
        },
        updateRoleUsers: async({commit, dispatch}, {roleId, users}) => {
            await loginApi.exec(LOGIN_API_TYPE.UPDATE_ROLE_USERS, {roleId, users})
            dispatch('loadUsersAndGroups')
        },
        getAuditLogsCount: async({commit}) => {
            return (await loginApi.exec(LOGIN_API_TYPE.AUDIT_LOGS_COUNT)).count
        },
        getAuditLogs: async({commit}, {from, count}) => {
            return await loginApi.exec(LOGIN_API_TYPE.GET_AUDIT_LOGS, {from, count})
        },
        loadUserPreferences: async({commit}) => {
            const preferences = await loginApi.exec(LOGIN_API_TYPE.GET_USER_PREFERENCES)
            commit('setPreferences', preferences)
        },
        updateUserPreference: async({commit}, {preference, value}) => {
            const pref = await loginApi.exec(LOGIN_API_TYPE.UPDATE_USER_PREFERENCE, {preference, value})
            commit('setPreferences', pref)
        }
    },
    getters: {
        user: state => state.user,
        groups: state => state.groups,
        users: state => state.users,
        roles: state => state.roles,
        globalPermissions: state => state.globalPermissions,
        userCan: (state, getters, rootState, rootGetters) => (permissionName, object) => {
            const globalPermissions = (state.globalPermissions && typeof state.globalPermissions === 'object')
                ? state.globalPermissions
                : {}
            if (Object.prototype.hasOwnProperty.call(globalPermissions, 'manage_graphs')) {
                return true
            }
            if (!object || !object.permissions) {
                return false
            }
            // const graphPermissions = rootGetters['repoStore/getGraphUserPermissions']
            // const workspacePermissions = rootGetters['repoStore/getWorkspaceUserPermissions']
            return object.permissions.includes(permissionName)
        },
        preferences: state => state.preferences
    }
}